import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Absolute http(s) URL only — rejects relative junk like "test". */
export function isValidHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;

  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function filenameFromUrl(fileUrl: string, fallback: string) {
  if (!isValidHttpUrl(fileUrl)) return fallback;

  try {
    const last = new URL(fileUrl).pathname.split('/').pop();
    if (last && last.includes('.')) return decodeURIComponent(last);
  } catch {
    // ignore malformed URLs
  }
  return fallback;
}

export async function downloadFile(fileUrl: string, filename: string) {
  if (!isValidHttpUrl(fileUrl)) {
    console.error('Error downloading file: invalid URL', fileUrl);
    return;
  }

  try {
    const response = await fetch(fileUrl);
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  } catch (error) {
    console.error('Error downloading file:', error);
    window.open(fileUrl, '_blank');
  }
}

function isWeekend(date: Date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/** Adds `days` weekdays (Mon–Fri). Public holidays are not skipped. */
export function addWorkingDays(date: Date, days: number): Date {
  const result = new Date(date);
  let remaining = days;
  const step = remaining >= 0 ? 1 : -1;

  while (remaining !== 0) {
    result.setDate(result.getDate() + step);
    if (!isWeekend(result)) remaining -= step;
  }

  return result;
}

export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function calendarDaysUntil(target: Date, from = new Date()): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.ceil((startOfLocalDay(target).getTime() - startOfLocalDay(from).getTime()) / msPerDay);
}

function formatShortDate(date: Date) {
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }).replace('.', '');
}

export function formatDeliveryWindow(shippingDateIso: string, workingDays = 5): string {
  const from = new Date(shippingDateIso);
  if (Number.isNaN(from.getTime())) return shippingDateIso;

  const to = addWorkingDays(from, workingDays);
  const year = to.getFullYear();
  return `${formatShortDate(from)} – ${formatShortDate(to)} ${year}`;
}